import type { DeliverableDraftUpdate } from "@/schema/deliverable"
import { downloadBlob } from "@/utils/download-helpers"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  downloadDeliverablePdf,
  publishDeliverable,
  updateDeliverableDraft,
  updatePlanningProfileVariables,
  type PlanningProfileVariableChange,
} from "./api"

export function useDownloadDeliverablePdf() {
  const getContext = useApiContext()
  return useMutation({
    mutationFn: async ({ id, label }: { id: string; label: string }) =>
      downloadDeliverablePdf(await getContext(), id, label),
    onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
    meta: { errorToast: { fallback: "Unable to download the deliverable PDF" } },
  })
}

export function usePublishDeliverable(id: string, actionLinkId?: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return publishDeliverable(context, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliverable", id] })
      if (actionLinkId) queryClient.invalidateQueries({ queryKey: ["action-link", actionLinkId] })
    },
  })
}

export function useUpdatePlanningProfileVariables(deliverableId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      planningProfileId,
      changes,
    }: {
      planningProfileId: string
      changes: PlanningProfileVariableChange[]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updatePlanningProfileVariables(context, planningProfileId, changes, deliverableId)
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["deliverable", deliverableId] }),
        queryClient.invalidateQueries({ queryKey: ["deliverableDraft", deliverableId] }),
      ]).then(() => undefined),
  })
}

export function useSaveDeliverableDraft(id: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (draft: DeliverableDraftUpdate) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateDeliverableDraft(context, id, draft)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliverable", id] })
      queryClient.invalidateQueries({ queryKey: ["deliverableDraft", id] })
    },
  })
}
